import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import api from '../services/api';
import { MessageSquare, Plus, Edit2, Trash2, Check, X } from 'lucide-react';

/**
 * ConversasPanel — exibe e gerencia o histórico de conversas de um lead.
 *
 * Props:
 *   leadId   (number)  — ID do lead
 *   readOnly (bool)    — se true, esconde os controles de edição (padrão: false)
 */
const ConversasPanel = forwardRef(function ConversasPanel({ leadId, readOnly = false }, ref) {
  const [conversas, setConversas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novoTexto, setNovoTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [editTexto, setEditTexto] = useState('');
  const [expandido, setExpandido] = useState(true);

  const carregar = useCallback(() => {
    if (!leadId) return;
    setLoading(true);
    api.get(`/leads/${leadId}/conversas/`)
      .then(r => setConversas(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => { carregar(); }, [carregar]);

  // Expõe salvarPendente() para o pai poder chamar via ref antes de fechar o modal
  useImperativeHandle(ref, () => ({
    salvarPendente: async () => {
      if (!novoTexto.trim()) return;
      setSalvando(true);
      try {
        await api.post(`/leads/${leadId}/conversas/`, { texto: novoTexto.trim() });
        setNovoTexto('');
        carregar();
      } catch (e) {
        alert('Erro ao salvar conversa: ' + (e.response?.data?.error || e.message || 'tente novamente'));
      } finally { setSalvando(false); }
    },
  }), [novoTexto, leadId, carregar]);

  const salvarNova = async () => {
    if (!novoTexto.trim()) return;
    setSalvando(true);
    try {
      await api.post(`/leads/${leadId}/conversas/`, { texto: novoTexto.trim() });
      setNovoTexto('');
      carregar();
    } catch (e) {
      alert('Erro ao salvar conversa: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    } finally { setSalvando(false); }
  };

  const iniciarEdicao = (c) => {
    setEditandoId(c.id);
    setEditTexto(c.texto);
  };

  const salvarEdicao = async (id) => {
    if (!editTexto.trim()) return;
    try {
      await api.put(`/leads/${leadId}/conversas/${id}/`, { texto: editTexto.trim() });
      setEditandoId(null);
      carregar();
    } catch (e) {
      alert('Erro ao editar conversa: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta conversa?')) return;
    try {
      await api.delete(`/leads/${leadId}/conversas/${id}/`);
      carregar();
    } catch (e) {
      alert('Erro ao excluir conversa: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    }
  };

  // ── Estilos ────────────────────────────────────────────────────────────────
  const S = {
    wrapper: {
      background: '#0a0f1a',
      border: '1px solid #1a2035',
      borderRadius: 10,
      overflow: 'hidden',
      marginBottom: 0,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      background: '#0e1420',
      borderBottom: expandido ? '1px solid #1a2035' : 'none',
      cursor: 'pointer',
      userSelect: 'none',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      fontSize: 12,
      fontWeight: 700,
      color: '#94a3b8',
      textTransform: 'uppercase',
      letterSpacing: 1,
    },
    badge: {
      background: 'rgba(246,201,14,0.15)',
      color: '#f6c90e',
      borderRadius: 10,
      padding: '1px 7px',
      fontSize: 11,
      fontWeight: 700,
    },
    body: {
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    item: {
      background: '#111827',
      border: '1px solid #1a2035',
      borderRadius: 8,
      padding: '10px 12px',
    },
    itemHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 6,
    },
    autor: {
      fontSize: 11,
      fontWeight: 700,
      color: '#60a5fa',
    },
    data: {
      fontSize: 10,
      color: '#475569',
    },
    texto: {
      fontSize: 13,
      color: '#cbd5e1',
      lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    acoes: {
      display: 'flex',
      gap: 6,
      marginTop: 8,
      justifyContent: 'flex-end',
    },
    btnIcon: (color = '#475569') => ({
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color,
      padding: '3px 5px',
      borderRadius: 5,
      display: 'flex',
      alignItems: 'center',
    }),
    textarea: {
      width: '100%',
      background: '#080c14',
      border: '1px solid #1a2035',
      borderRadius: 7,
      padding: '8px 10px',
      color: '#e2e8f0',
      fontSize: 13,
      resize: 'vertical',
      minHeight: 72,
      outline: 'none',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    btnSalvar: {
      padding: '7px 16px',
      borderRadius: 7,
      border: 'none',
      background: 'rgba(74,222,128,0.15)',
      color: '#4ade80',
      fontSize: 12,
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 5,
    },
    btnCancelar: {
      padding: '7px 12px',
      borderRadius: 7,
      border: '1px solid #1a2035',
      background: 'transparent',
      color: '#64748b',
      fontSize: 12,
      cursor: 'pointer',
    },
  };

  return (
    <div style={S.wrapper}>
      {/* Cabeçalho clicável */}
      <div style={S.header} onClick={() => setExpandido(e => !e)}>
        <div style={S.headerLeft}>
          <MessageSquare size={13} />
          Conversas
          {conversas.length > 0 && <span style={S.badge}>{conversas.length}</span>}
        </div>
        <span style={{ color: '#475569', fontSize: 12 }}>{expandido ? '▲' : '▼'}</span>
      </div>

      {expandido && (
        <div style={S.body}>
          {/* Histórico */}
          {loading && <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '8px 0' }}>Carregando...</div>}

          {!loading && conversas.length === 0 && (
            <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '8px 0' }}>
              {readOnly ? 'Nenhuma conversa registrada.' : 'Nenhuma conversa ainda. Adicione a primeira abaixo.'}
            </div>
          )}

          {conversas.map(c => (
            <div key={c.id} style={S.item}>
              <div style={S.itemHeader}>
                <span style={S.autor}>{c.autor_nome}</span>
                <span style={S.data}>{c.criado_em}{c.atualizado_em !== c.criado_em ? ' · editado' : ''}</span>
              </div>

              {/* Modo edição */}
              {!readOnly && editandoId === c.id ? (
                <>
                  <textarea
                    style={S.textarea}
                    value={editTexto}
                    onChange={e => setEditTexto(e.target.value)}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                    <button style={S.btnCancelar} onClick={() => setEditandoId(null)}>Cancelar</button>
                    <button style={S.btnSalvar} onClick={() => salvarEdicao(c.id)}>
                      <Check size={12} /> Salvar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={S.texto}>{c.texto}</div>
                  {!readOnly && (
                    <div style={S.acoes}>
                      <button style={S.btnIcon('#60a5fa')} title="Editar" onClick={() => iniciarEdicao(c)}>
                        <Edit2 size={13} />
                      </button>
                      <button style={S.btnIcon('#f87171')} title="Excluir" onClick={() => excluir(c.id)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Formulário nova conversa */}
          {!readOnly && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: conversas.length > 0 ? 4 : 0 }}>
              <textarea
                style={S.textarea}
                placeholder="Como foi a conversa? Descreva aqui..."
                value={novoTexto}
                onChange={e => setNovoTexto(e.target.value)}
                onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') salvarNova(); }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  style={{ ...S.btnSalvar, opacity: (!novoTexto.trim() || salvando) ? 0.5 : 1, cursor: (!novoTexto.trim() || salvando) ? 'not-allowed' : 'pointer' }}
                  onClick={salvarNova}
                  disabled={!novoTexto.trim() || salvando}
                >
                  <Plus size={12} />
                  {salvando ? 'Salvando...' : 'Adicionar conversa'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ConversasPanel;
